import * as React from 'react';
import { memo, useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import {
  SearchNames,
  usePopupStateOperations,
  usePublishToLms,
  useRemoveFromLmsData,
  useSearch,
  useToasts,
} from 'core/hooks';
import { PopupNames } from 'core';
import {
  GlobalToastTypeNames,
  PublishedLmsType,
  ToastTypes,
} from 'components/types';

import { RemoveGroupFromLMSPopupComponent } from './RemoveGroupFromLMSPopup';

export const RemoveGroupFromLMSPopup = memo(() => {
  const { t } = useTranslation();
  const { addToast } = useToasts();
  const {
    operations: { hidePopup },
  } = usePopupStateOperations();
  const { setSearchQuery } = useSearch(SearchNames.settings);

  const removeFromLmsData = useRemoveFromLmsData();

  const [
    publishToLms,
    { success, loading, error: serverErrors },
  ] = usePublishToLms();

  const onClose = useCallback(() => hidePopup(PopupNames.removeGroupFromLMS), [
    hidePopup,
  ]);

  const onConfirm = useCallback(() => {
    if (!loading && removeFromLmsData?.id) {
      const publishParams = {
        [PublishedLmsType.student]: !removeFromLmsData.publishedLms,
        [PublishedLmsType.teacher]: !removeFromLmsData.publishedLmsForTeacher,
        [PublishedLmsType.methodist]: !removeFromLmsData.publishedLmsForMethodist,
      };

      publishToLms({
        groupId: removeFromLmsData.id,
        publishedLms: removeFromLmsData.publishedLmsType
          ? publishParams[removeFromLmsData.publishedLmsType]
          : false,
        publishedForTeachers:
          removeFromLmsData.publishedLmsType === PublishedLmsType.teacher,
        publishedLmsForMethodist:
          removeFromLmsData.publishedLmsType === PublishedLmsType.methodist,
      });
    }
  }, [publishToLms, loading, removeFromLmsData]);

  // Перепроверка G7: заголовок — переводом, как у `DeleteGroupPopup` (F27),
  // а не английским литералом.
  useEffect(() => {
    if (serverErrors) {
      addToast({
        data: {
          title: `${t('removeGroupPopup.failed')}`,
          text: serverErrors.message,
          type: ToastTypes.error,
        },
        type: GlobalToastTypeNames.GroupFromLMSRemove,
      });
    }
  }, [serverErrors, addToast, t]);

  useEffect(() => {
    if (success) {
      setSearchQuery('');
      addToast({
        data: {
          title: `${t('removeGroupPopup.success')}`,
          type: ToastTypes.success,
        },
        type: GlobalToastTypeNames.GroupFromLMSRemove,
      });
      onClose();
    }
  }, [onClose, success, setSearchQuery, addToast, t]);

  return (
    <RemoveGroupFromLMSPopupComponent
      onClose={onClose}
      onConfirm={onConfirm}
      groupName={removeFromLmsData ? removeFromLmsData.group : ''}
      publishedLmsType={removeFromLmsData && removeFromLmsData.publishedLmsType}
    />
  );
});
